import type { CSSProperties } from 'react';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';

interface VideoDetails {
    title?: string;
    tournamentName?: string;
    team1Name?: string;
    team2Name?: string;
    gameRound?: string;
    source: { name: string };
    url: string;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="py-1.5">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-sm">{value}</div>
        </div>
    );
}

function VideoDetailsDialog(props: { open: boolean; onClose: () => void; video: VideoDetails }) {
    const { onClose, open } = props;

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="min-w-[350px]">
                <DialogTitle>Video details</DialogTitle>
                <div className="divide-y">
                    <DetailRow label="Title" value={props.video.title} />
                    <DetailRow label="Tournament" value={props.video.tournamentName} />
                    <DetailRow label="Team 1" value={props.video.team1Name} />
                    <DetailRow label="Team 2" value={props.video.team2Name} />
                    {props.video.gameRound && (
                        <DetailRow label="Game round" value={props.video.gameRound} />
                    )}
                    <DetailRow label="Source" value={props.video.source.name} />
                    <DetailRow
                        label="URL"
                        value={
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href={props.video.url}
                                className="text-primary underline-offset-4 hover:underline">
                                {props.video.url}
                            </a>
                        }
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Rendered by lib/DataTable for every row with a `videos` field -- keep this
// default export's shape (props: `video`, `sx`) exactly, since DataTable
// (28 consumers) imports it directly and is out of this phase's scope.
export default function VideoDetailsMenu(props: { video: VideoDetails; sx?: CSSProperties }) {
    const [open, setOpen] = useState(false);

    return (
        <div style={props.sx}>
            <button
                type="button"
                aria-label="Video details"
                onClick={() => setOpen(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full p-1 text-foreground/70 hover:bg-muted hover:text-foreground">
                <Menu className="h-4 w-4" />
            </button>
            <VideoDetailsDialog open={open} onClose={() => setOpen(false)} video={props.video} />
        </div>
    );
}
